/**
 * useClonedVoices Hook
 *
 * Manages cloned voices state and operations.
 * Handles voice listing, cloning, updating, and deletion.
 *
 * React 19 patterns:
 * - useReducer for complex state (6+ related states)
 * - useCallback for event handlers
 * - useMemo for derived values
 */

import { useCallback, useMemo, useReducer } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import {
  IClonedVoice,
  IVoiceCloneFormInput,
  IVoiceSettings,
  IAudioFileUpload,
  VoiceCloneStep,
  VOICE_CONSTANTS,
} from '@/@types/voices';

// ============================================
// STATE TYPES
// ============================================

interface ClonedVoicesState {
  // Cloning workflow
  cloneStep: VoiceCloneStep;
  isCloning: boolean;
  cloneError: string | null;
  cloneErrorCode: string | null;

  // Deletion workflow
  isDeleting: boolean;
  deletingVoiceId: Id<'clonedVoices'> | null;
  deleteError: string | null;

  // Settings update
  isUpdatingSettings: boolean;
  updatingVoiceId: Id<'clonedVoices'> | null;

  // Form state
  uploadProgress: number; // 0-100
}

type ClonedVoicesAction =
  | { type: 'START_CLONE' }
  | { type: 'CLONE_VALIDATING' }
  | { type: 'CLONE_UPLOADING'; payload: { progress: number } }
  | { type: 'CLONE_PROCESSING' }
  | { type: 'CLONE_SUCCESS' }
  | { type: 'CLONE_ERROR'; payload: { error: string; errorCode?: string } }
  | { type: 'START_DELETE'; payload: { voiceId: Id<'clonedVoices'> } }
  | { type: 'DELETE_SUCCESS' }
  | { type: 'DELETE_ERROR'; payload: { error: string } }
  | { type: 'START_UPDATE_SETTINGS'; payload: { voiceId: Id<'clonedVoices'> } }
  | { type: 'UPDATE_SETTINGS_SUCCESS' }
  | { type: 'UPDATE_SETTINGS_ERROR' }
  | { type: 'RESET' };

// ============================================
// REDUCER
// ============================================

const initialState: ClonedVoicesState = {
  cloneStep: 'idle',
  isCloning: false,
  cloneError: null,
  cloneErrorCode: null,
  isDeleting: false,
  deletingVoiceId: null,
  deleteError: null,
  isUpdatingSettings: false,
  updatingVoiceId: null,
  uploadProgress: 0,
};

function clonedVoicesReducer(
  state: ClonedVoicesState,
  action: ClonedVoicesAction
): ClonedVoicesState {
  switch (action.type) {
    case 'START_CLONE':
      return {
        ...state,
        cloneStep: 'validating',
        isCloning: true,
        cloneError: null,
        cloneErrorCode: null,
        uploadProgress: 0,
      };

    case 'CLONE_VALIDATING':
      return {
        ...state,
        cloneStep: 'validating',
      };

    case 'CLONE_UPLOADING':
      return {
        ...state,
        cloneStep: 'uploading',
        uploadProgress: action.payload.progress,
      };

    case 'CLONE_PROCESSING':
      return {
        ...state,
        cloneStep: 'cloning',
        uploadProgress: 100,
      };

    case 'CLONE_SUCCESS':
      return {
        ...state,
        cloneStep: 'completed',
        isCloning: false,
        uploadProgress: 100,
      };

    case 'CLONE_ERROR':
      return {
        ...state,
        cloneStep: 'error',
        isCloning: false,
        cloneError: action.payload.error,
        cloneErrorCode: action.payload.errorCode || null,
      };

    case 'START_DELETE':
      return {
        ...state,
        isDeleting: true,
        deletingVoiceId: action.payload.voiceId,
        deleteError: null,
      };

    case 'DELETE_SUCCESS':
      return {
        ...state,
        isDeleting: false,
        deletingVoiceId: null,
      };

    case 'DELETE_ERROR':
      return {
        ...state,
        isDeleting: false,
        deleteError: action.payload.error,
      };

    case 'START_UPDATE_SETTINGS':
      return {
        ...state,
        isUpdatingSettings: true,
        updatingVoiceId: action.payload.voiceId,
      };

    case 'UPDATE_SETTINGS_SUCCESS':
      return {
        ...state,
        isUpdatingSettings: false,
        updatingVoiceId: null,
      };

    case 'UPDATE_SETTINGS_ERROR':
      return {
        ...state,
        isUpdatingSettings: false,
        updatingVoiceId: null,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert File to base64 encoded string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:audio/mpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Convert array of Files to IAudioFileUpload array
 */
async function prepareAudioFiles(files: File[]): Promise<IAudioFileUpload[]> {
  const prepared: IAudioFileUpload[] = [];

  for (const file of files) {
    const base64 = await fileToBase64(file);
    prepared.push({
      base64,
      mimeType: file.type || 'audio/mpeg',
      filename: file.name,
    });
  }

  return prepared;
}

/**
 * Validate audio files before upload
 */
function validateAudioFiles(files: File[]): { valid: boolean; error?: string } {
  // Check file count
  if (files.length < VOICE_CONSTANTS.MIN_AUDIO_FILES) {
    return {
      valid: false,
      error: `At least ${VOICE_CONSTANTS.MIN_AUDIO_FILES} audio file is required`,
    };
  }

  if (files.length > VOICE_CONSTANTS.MAX_AUDIO_FILES) {
    return {
      valid: false,
      error: `Maximum ${VOICE_CONSTANTS.MAX_AUDIO_FILES} audio files allowed`,
    };
  }

  // Check file sizes and types
  for (const file of files) {
    if (file.size > VOICE_CONSTANTS.MAX_AUDIO_FILE_SIZE_BYTES) {
      return {
        valid: false,
        error: `File "${file.name}" exceeds maximum size of ${VOICE_CONSTANTS.MAX_AUDIO_FILE_SIZE_MB}MB`,
      };
    }

    // Check MIME type
    const isValidType = VOICE_CONSTANTS.ACCEPTED_AUDIO_TYPES.some(
      type => file.type === type || file.type.startsWith('audio/')
    );

    if (!isValidType && file.type) {
      return {
        valid: false,
        error: `File "${file.name}" has unsupported format. Accepted: MP3, WAV, M4A, WebM, OGG`,
      };
    }
  }

  return { valid: true };
}

// ============================================
// HOOK
// ============================================

export function useClonedVoices(
  options: {
    onCloneSuccess?: (voiceId: Id<'clonedVoices'>) => void;
    onCloneError?: (error: string) => void;
    onDeleteSuccess?: () => void;
    onDeleteError?: (error: string) => void;
  } = {}
) {
  const { onCloneSuccess, onCloneError, onDeleteSuccess, onDeleteError } = options;

  // ============================================
  // STATE
  // ============================================
  const [state, dispatch] = useReducer(clonedVoicesReducer, initialState);

  // ============================================
  // CONVEX QUERIES
  // ============================================

  // Fetch all active cloned voices for current user
  const voices = useQuery(api.voices.getActiveVoices);

  // Fetch voice count for limit checking
  const voiceCount = useQuery(api.voices.getVoiceCount);

  // ============================================
  // CONVEX MUTATIONS & ACTIONS
  // ============================================

  const cloneVoiceAction = useAction(api.ai.voiceCloning.cloneVoice);
  const deleteVoiceAction = useAction(api.ai.voiceCloning.deleteVoiceFromElevenLabs);
  const regeneratePreviewAction = useAction(api.ai.voiceCloning.regeneratePreview);
  const updateVoiceSettingsMutation = useMutation(api.voices.updateVoiceSettings);
  const updateVoiceMutation = useMutation(api.voices.updateVoice);
  const softDeleteVoiceMutation = useMutation(api.voices.softDeleteVoice);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const isLoading = useMemo(() => voices === undefined, [voices]);

  const activeVoices = useMemo(() => {
    if (!voices) return [];
    return voices.filter((v: { status: string }) => v.status === 'active');
  }, [voices]);

  const creatingVoices = useMemo(() => {
    if (!voices) return [];
    return voices.filter((v: { status: string }) => v.status === 'creating');
  }, [voices]);

  const canCreateVoice = useMemo(() => {
    if (voiceCount === undefined) return false;
    return voiceCount < VOICE_CONSTANTS.MAX_VOICES_PER_USER;
  }, [voiceCount]);

  const remainingVoiceSlots = useMemo(() => {
    if (voiceCount === undefined) return 0;
    return Math.max(0, VOICE_CONSTANTS.MAX_VOICES_PER_USER - voiceCount);
  }, [voiceCount]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Clone a new voice from audio files
   */
  const handleCloneVoice = useCallback(
    async (input: IVoiceCloneFormInput): Promise<Id<'clonedVoices'> | null> => {
      dispatch({ type: 'START_CLONE' });

      // 1. Validate name
      const trimmedName = input.name.trim();
      if (!trimmedName) {
        dispatch({
          type: 'CLONE_ERROR',
          payload: { error: 'Voice name is required', errorCode: 'INVALID_NAME' },
        });
        toast.error('Voice name is required');
        onCloneError?.('Voice name is required');
        return null;
      }

      if (trimmedName.length > VOICE_CONSTANTS.MAX_NAME_LENGTH) {
        dispatch({
          type: 'CLONE_ERROR',
          payload: {
            error: `Name must be ${VOICE_CONSTANTS.MAX_NAME_LENGTH} characters or less`,
            errorCode: 'NAME_TOO_LONG',
          },
        });
        toast.error(`Name must be ${VOICE_CONSTANTS.MAX_NAME_LENGTH} characters or less`);
        onCloneError?.(`Name must be ${VOICE_CONSTANTS.MAX_NAME_LENGTH} characters or less`);
        return null;
      }

      // 2. Validate description
      if (input.description && input.description.length > VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
        dispatch({
          type: 'CLONE_ERROR',
          payload: {
            error: `Description must be ${VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters or less`,
            errorCode: 'DESCRIPTION_TOO_LONG',
          },
        });
        toast.error(
          `Description must be ${VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters or less`
        );
        onCloneError?.(
          `Description must be ${VOICE_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters or less`
        );
        return null;
      }

      // 3. Validate audio files
      const validation = validateAudioFiles(input.audioFiles);
      if (!validation.valid) {
        dispatch({
          type: 'CLONE_ERROR',
          payload: { error: validation.error || 'Invalid audio files', errorCode: 'INVALID_FILES' },
        });
        toast.error(validation.error || 'Invalid audio files');
        onCloneError?.(validation.error || 'Invalid audio files');
        return null;
      }

      // 4. Check voice limit
      if (!canCreateVoice) {
        dispatch({
          type: 'CLONE_ERROR',
          payload: {
            error: `Maximum ${VOICE_CONSTANTS.MAX_VOICES_PER_USER} voices allowed`,
            errorCode: 'LIMIT_REACHED',
          },
        });
        toast.error(`Maximum ${VOICE_CONSTANTS.MAX_VOICES_PER_USER} voices allowed`);
        onCloneError?.(`Maximum ${VOICE_CONSTANTS.MAX_VOICES_PER_USER} voices allowed`);
        return null;
      }

      try {
        // 5. Convert files to base64
        dispatch({ type: 'CLONE_UPLOADING', payload: { progress: 10 } });

        const audioFiles = await prepareAudioFiles(input.audioFiles);

        dispatch({ type: 'CLONE_UPLOADING', payload: { progress: 50 } });

        // 6. Call clone action
        dispatch({ type: 'CLONE_PROCESSING' });

        const result = await cloneVoiceAction({
          name: trimmedName,
          description: input.description?.trim(),
          audioFiles,
        });

        if (!result.success || !result.voiceId) {
          dispatch({
            type: 'CLONE_ERROR',
            payload: {
              error: result.error || 'Voice cloning failed',
              errorCode: result.errorCode,
            },
          });
          toast.error('Voice cloning failed', {
            description: result.error || 'Please try again',
          });
          onCloneError?.(result.error || 'Voice cloning failed');
          return null;
        }

        // 7. Success
        dispatch({ type: 'CLONE_SUCCESS' });
        toast.success('Voice cloned successfully!', {
          description: `"${trimmedName}" is ready to use`,
        });
        onCloneSuccess?.(result.voiceId);
        return result.voiceId;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({
          type: 'CLONE_ERROR',
          payload: { error: errorMessage, errorCode: 'INTERNAL_ERROR' },
        });
        toast.error('Voice cloning failed', {
          description: errorMessage,
        });
        onCloneError?.(errorMessage);
        return null;
      }
    },
    [canCreateVoice, cloneVoiceAction, onCloneSuccess, onCloneError]
  );

  /**
   * Delete a cloned voice (from ElevenLabs and database)
   */
  const handleDeleteVoice = useCallback(
    async (voiceId: Id<'clonedVoices'>): Promise<boolean> => {
      dispatch({ type: 'START_DELETE', payload: { voiceId } });

      try {
        const result = await deleteVoiceAction({ voiceId });

        if (!result.success) {
          dispatch({
            type: 'DELETE_ERROR',
            payload: { error: result.error || 'Failed to delete voice' },
          });
          toast.error('Failed to delete voice', {
            description: result.error,
          });
          onDeleteError?.(result.error || 'Failed to delete voice');
          return false;
        }

        dispatch({ type: 'DELETE_SUCCESS' });
        toast.success('Voice deleted successfully');
        onDeleteSuccess?.();
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({
          type: 'DELETE_ERROR',
          payload: { error: errorMessage },
        });
        toast.error('Failed to delete voice', {
          description: errorMessage,
        });
        onDeleteError?.(errorMessage);
        return false;
      }
    },
    [deleteVoiceAction, onDeleteSuccess, onDeleteError]
  );

  /**
   * Soft delete a voice (mark as deleted without removing from ElevenLabs)
   */
  const handleSoftDeleteVoice = useCallback(
    async (voiceId: Id<'clonedVoices'>): Promise<boolean> => {
      dispatch({ type: 'START_DELETE', payload: { voiceId } });

      try {
        const result = await softDeleteVoiceMutation({ voiceId });

        if (!result.success) {
          dispatch({
            type: 'DELETE_ERROR',
            payload: { error: result.error || 'Failed to delete voice' },
          });
          toast.error('Failed to delete voice', {
            description: result.error,
          });
          onDeleteError?.(result.error || 'Failed to delete voice');
          return false;
        }

        dispatch({ type: 'DELETE_SUCCESS' });
        toast.success('Voice removed');
        onDeleteSuccess?.();
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        dispatch({
          type: 'DELETE_ERROR',
          payload: { error: errorMessage },
        });
        toast.error('Failed to delete voice', {
          description: errorMessage,
        });
        onDeleteError?.(errorMessage);
        return false;
      }
    },
    [softDeleteVoiceMutation, onDeleteSuccess, onDeleteError]
  );

  /**
   * Update voice settings (stability, similarity boost, style)
   */
  const handleUpdateSettings = useCallback(
    async (voiceId: Id<'clonedVoices'>, settings: IVoiceSettings): Promise<boolean> => {
      dispatch({ type: 'START_UPDATE_SETTINGS', payload: { voiceId } });

      try {
        const result = await updateVoiceSettingsMutation({ voiceId, voiceSettings: settings });

        if (!result.success) {
          dispatch({ type: 'UPDATE_SETTINGS_ERROR' });
          toast.error('Failed to update voice settings', {
            description: result.error,
          });
          return false;
        }

        dispatch({ type: 'UPDATE_SETTINGS_SUCCESS' });
        toast.success('Voice settings updated');
        return true;
      } catch {
        dispatch({ type: 'UPDATE_SETTINGS_ERROR' });
        toast.error('Failed to update voice settings');
        return false;
      }
    },
    [updateVoiceSettingsMutation]
  );

  /**
   * Update voice name or description
   */
  const handleUpdateVoice = useCallback(
    async (
      voiceId: Id<'clonedVoices'>,
      updates: { name?: string; description?: string }
    ): Promise<boolean> => {
      try {
        const result = await updateVoiceMutation({ voiceId, ...updates });

        if (!result.success) {
          toast.error('Failed to update voice', {
            description: result.error,
          });
          return false;
        }

        toast.success('Voice updated');
        return true;
      } catch {
        toast.error('Failed to update voice');
        return false;
      }
    },
    [updateVoiceMutation]
  );

  /**
   * Regenerate preview audio for a voice
   * Use this for voices that don't have a preview URL
   */
  const handleRefreshPreview = useCallback(
    async (voiceId: Id<'clonedVoices'>): Promise<void> => {
      try {
        toast.loading('Generating preview...', { id: `preview-${voiceId}` });

        const result = await regeneratePreviewAction({ voiceId });

        if (!result.success) {
          toast.error('Failed to generate preview', {
            id: `preview-${voiceId}`,
            description: result.error,
          });
          return;
        }

        toast.success('Preview generated!', {
          id: `preview-${voiceId}`,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error('Failed to generate preview', {
          id: `preview-${voiceId}`,
          description: errorMessage,
        });
      }
    },
    [regeneratePreviewAction]
  );

  /**
   * Reset state
   */
  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  /**
   * Get a specific voice by ID from the loaded voices
   */
  const getVoiceById = useCallback(
    (voiceId: Id<'clonedVoices'>): IClonedVoice | undefined => {
      if (!voices) return undefined;
      return voices.find((v: { _id: Id<'clonedVoices'> }) => v._id === voiceId) as
        | IClonedVoice
        | undefined;
    },
    [voices]
  );

  // ============================================
  // RETURN API
  // ============================================

  return {
    // State
    state,
    cloneStep: state.cloneStep,
    isCloning: state.isCloning,
    cloneError: state.cloneError,
    cloneErrorCode: state.cloneErrorCode,
    isDeleting: state.isDeleting,
    deletingVoiceId: state.deletingVoiceId,
    deleteError: state.deleteError,
    isUpdatingSettings: state.isUpdatingSettings,
    updatingVoiceId: state.updatingVoiceId,
    uploadProgress: state.uploadProgress,

    // Data
    voices: voices as IClonedVoice[] | undefined,
    activeVoices: activeVoices as IClonedVoice[],
    creatingVoices: creatingVoices as IClonedVoice[],
    voiceCount: voiceCount ?? 0,
    isLoading,

    // Computed
    canCreateVoice,
    remainingVoiceSlots,

    // Actions
    cloneVoice: handleCloneVoice,
    deleteVoice: handleDeleteVoice,
    softDeleteVoice: handleSoftDeleteVoice,
    updateSettings: handleUpdateSettings,
    updateVoice: handleUpdateVoice,
    refreshPreview: handleRefreshPreview,
    getVoiceById,
    reset: handleReset,
  };
}
